import axios from 'axios';
import context from '../context';
import { UserService } from 'frilans-finans';
import { loginSuccess, loginFailure } from '../actions';
import { setReauthenticationStatus } from 'redux/modules/auth/index';
import { replaceBackendUri } from 'frilans-finans/replace-backend-uri';
import TaxkeyService from 'frilans-finans/taxkeys/taxkey-service';
import reduxStore, { history } from 'store';
import { clearUser } from 'redux/modules/user/index';
import { LocalStorageStore } from './local-storage-store';
import CookieUtils from 'frilans-finans/utils/cookie-utils';
import PendingRequestEntity from 'frilans-finans/entities/pending-request-entity';
import * as variables from 'frilans-finans/variables';

export class AuthenticationService {

  constructor () {
    this.store = new LocalStorageStore();
    this.oauthToken = this.store.getOauth();
    this._refreshTokenPromise = null;

    axios.defaults.headers.common['Accept'] = 'application/vnd.api.v2+json';
    axios.interceptors.request.use(replaceBackendUri);
  }

  get isAuthenticated () {
    return !this.store.isEmpty;
  }

  logout () {
    this.clearUserInfo();
    history.push('/login');
  }

  logoutWithoutRedirect () {
    this.clearUserInfo();
  }

  clearUserInfo () {
    this.oauthToken = null;
    this.store.reset();
    CookieUtils.removeNameCookie();
    UserService.clearUser();
    reduxStore.dispatch(clearUser());
  }

  login (identity, password) {
    return axios.post('backend://auth', {
      'grant_type': 'password',
      'username': identity,
      'password': password,
      'scope': 'client'
    }).then(
      (response) => this._onSuccessfulAuthentication(response.data),
      (response) => this._onFailedAuthentication(response)
    );
  }

  sendMagicLoginLink (identity, password) {
    return axios.post('backend://email-login-links', {
      'data': {
        'attributes': {
          'username': identity,
          'password': password
        }
      }
    }).then((response) => response);
  }

  linkLogin (token) {
    return axios.post(`backend://token-login/${token}`)
      .then((response) => {
        return this._onSuccessfulAuthentication(response.data);
      })
      .catch((error) => {
        return error.response;
      });
  }

  eidLinkLogin (code) {
    return axios.post(`${variables.eidUrl}/exchange`, {
      'code': code
    }).then((response) => {
      return this._onSuccessfulAuthentication({
        'access_token': response.data.access_token,
        'expires_in': 604800,
        'refresh_token': response.data.access_token
      });
    })
    .catch((error) => {
      return error.response;
    });
  }

  eidLinkRegister (code) {
    return axios.post(`${variables.eidUrl}/create`, {
      'code': code
    }).then((response) => {
      return this._onSuccessfulAuthentication({
        'access_token': response.data.access_token,
        'expires_in': 604800,
        'refresh_token': response.data.access_token
      });
    })
    .catch((error) => {
      return error.response;
    });
  }

  loginWithBankId (sessionId, createUser) {
    let pendingRequest = new PendingRequestEntity();

    let params = {
      'grant_type': 'bankid',
      'session_id': sessionId,
      'scope': 'client'
    };

    if (createUser) {
      params.create_user = 'true';
    }

    pendingRequest.promise = axios.post(
      'backend://auth',
      params,
      {
        cancelToken: new axios.CancelToken(function executor (c) {
          pendingRequest.cancel = c;
        })
      }
    ).then(
      (response) => this._onSuccessfulAuthentication(response.data),
      (response) => this._onFailedAuthentication(response)
    );

    return pendingRequest;
  }

  getUser () {
    UserService.get().promise
      .then(
      (user) => {
        TaxkeyService.get(user.id);

        if (typeof this.routerCallback === 'function') {
          this.routerCallback(user);
        }
      },
      (error) => console.log(error)
    );
  }

  /**
   * Inject a request interceptor
   *
   * This interceptor handles injecting the oauth token into the proper requests
   *
   * @param config
   * @returns {*}
   * @private
   */
  _requestInterceptor = (config) => {
    if (this.store.isEmpty) {
      return config;
    }

    config.headers['Authorization'] = 'Bearer ' + this.oauthToken.accessToken;

    return config;
  }

  /**
   *
   * @param response
   * @returns {*}
   * @private
   */
  _responseInterceptor = (error) => {
    if (this.shouldRefreshToken(this.oauthToken, error) && !this.hasRefreshedToken(error)) {
      return this.refreshToken()
        .then((response) => {
          return axios(error.config);
        });
    }
    return Promise.reject(error);
  }

  shouldRefreshToken = (token, error) => {
    const noResponse = error.response === undefined;
    const unauthorized = !noResponse && (error.response.status || null) === 401;

    if (error.config !== undefined &&
      JSON.stringify(error.config.url).indexOf(variables.apiUrl) === -1) {
      return false;
    }

    return token !== null && (noResponse || unauthorized);
  }

  hasRefreshedToken = (error) => {
    try {
      let responseJson = JSON.parse(error.response.config.data);
      if (responseJson.grant_type === 'refresh_token') {
        context.store.dispatch(setReauthenticationStatus('fail'));
        this._refreshTokenPromise = null;
        this.logout();
        return true;
      }
    } catch (e) {
      return false;
    }
    return false;
  }

  refreshToken () {
    if (this._refreshTokenPromise !== null) {
      return this._refreshTokenPromise;
    }

    this._refreshTokenPromise = axios.post('backend://auth', {
      'grant_type': 'refresh_token',
      'refresh_token': this.oauthToken.refreshToken
    })
      .then((response) => {
        let token = response.data;

        this.oauthToken = {
          accessToken: token.access_token,
          expiresIn: token.expires_in,
          refreshToken: token.refresh_token
        };

        this.store.persist(this.oauthToken);
        this._refreshTokenPromise = null;
        CookieUtils.updateNameCookie();
      })
      .catch((e) => {
        context.store.dispatch(setReauthenticationStatus('fail'));
        this._refreshTokenPromise = null;
        this.logout();
        throw e;
      });

    return this._refreshTokenPromise;
  }

  /**
   * Handle a successful authentication
   *
   * @param token
   * @private
   */
  _onSuccessfulAuthentication (token) {
    this.oauthToken = {
      accessToken: token.access_token,
      expiresIn: token.expires_in,
      refreshToken: token.refresh_token
    };

    this.store.persist(this.oauthToken);
    context.store.dispatch(loginSuccess());
    this.getUser();
    return token;
  }

  /**
   * Handle a authentication failure
   *
   * @param error
   * @returns {Promise}
   * @private
   */
  _onFailedAuthentication (error) {
    context.store.dispatch(loginFailure());
    return Promise.reject(error.response);
  }

  /**
   * Used by the token login view to login with query parameters
   */
  tokenLogin (accessToken, expiresIn) {
    this.oauthToken = {
      accessToken: accessToken,
      expiresIn: expiresIn,
      refreshToken: null
    };

    this.store.persist(this.oauthToken);
  }

  setRouterCallback (callback) {
    this.routerCallback = callback;
  }

}

export default new AuthenticationService();



/** WEBPACK FOOTER **
 ** ./src/interactive-solutions/authentication/service/authentication.js
 **/